import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Hourglass, Loader2 } from 'lucide-react'
import { equipmentApi, type PendingActionRow } from '@/api/equipmentApi'
import { useEquipmentScope } from '@/components/equipment/equipmentScope'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { PENDING_ACTIONS_KEY, usePendingActions } from '@/hooks/usePendingActions'
import { useSortedRows } from '@/hooks/useSortedRows'
import { useTableColumns } from '@/hooks/useTableColumns'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import { procedureRunPath } from '@/lib/procedureRunPath'
import { requestErrorMessage } from '@/lib/requestErrorMessage'
import { formatDueDay } from '@/lib/unitMaintenance'
import { keepRowOrder } from '@/lib/keepRowOrder'
import { DueDateEditor } from '@/components/equipment/DueDateEditor'
import { useAuth } from '@/providers/AuthProvider'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { cn } from '@/lib/utils'
import { useTenant } from '@/providers/TenantProvider'
import { useResourceFilter } from '@/hooks/useResourceFilter'
import { ResourceFilter } from '@/components/equipment/ResourceFilter'

/**
 * Cleaning / Equipment → Pending Actions (z8nrz7ewt1): the tasks whose cycle
 * carries a procedure, until the task ends. The counterpart of Inventory's
 * Pending tab, for rooms and equipment.
 *
 * Opening a row opens its procedure run — created the first time, the same one
 * after — on the run page, where its forms are filled in. Completing the last
 * of them completes the task (z8nrz7fgzw), and its row leaves by itself.
 *
 * The Resource filter narrows it to one person's tasks: the viewer's own by
 * default, unless they are Super Admin or a supervisor.
 */
export function PendingActionsTab() {
  const [resourceId, setResourceId] = useResourceFilter()
  const { data, isLoading, error } = usePendingActions(true, resourceId)
  const { copy } = useEquipmentScope()

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-xs text-muted-foreground'>
          Tasks whose cycle carries a procedure, until the task ends. Open a row to work its procedure's forms;
          completing them completes the task.
        </p>
        <ResourceFilter value={resourceId} onChange={setResourceId} />
      </div>
      {isLoading ? (
        <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading pending actions…
        </div>
      ) : error ? (
        <p className='text-sm text-destructive'>{requestErrorMessage(error, 'Could not load pending actions.')}</p>
      ) : (
        <PendingActionsTable
          key={resourceId ?? 'all'}
          rows={data ?? []}
          unitLabel={copy.unit}
          typeLabel={copy.type}
        />
      )}
    </div>
  )
}

const COLUMNS: TableColumnDef[] = [
  { id: 'unit', header: 'Unit', locked: true, sortable: true },
  { id: 'type', header: 'Type', sortable: true },
  { id: 'procedure', header: 'Procedure', sortable: true },
  { id: 'cycle', header: 'Cycle', sortable: true },
  { id: 'progress', header: 'Form progress', sortable: true },
  { id: 'due', header: 'Due', sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'assigned', header: 'Assigned', sortable: true },
  { id: 'actions', header: 'Open', locked: true },
]

function progressPct(row: PendingActionRow): number {
  return row.totalForms > 0 ? Math.round((row.completedForms / row.totalForms) * 100) : 0
}

/** Started, overdue (how long), or simply due — overdue counts a started task too. */
function statusOf(row: PendingActionRow): { label: string; className: string } {
  if (row.overdue) {
    return {
      label: `Overdue${row.daysOverdue ? ` ${row.daysOverdue}d` : ''}${row.status === 'STARTED' ? ' · started' : ''}`,
      className: 'bg-red-100 text-red-700',
    }
  }
  if (row.status === 'STARTED') return { label: 'Started', className: 'bg-blue-100 text-blue-700' }
  return { label: 'Due', className: 'bg-amber-100 text-amber-800' }
}

const SORT: SortAccessors<PendingActionRow> = {
  unit: r => r.equipmentLabel,
  type: r => r.equipmentTypeName,
  procedure: r => r.procedureTemplateName,
  cycle: r => r.cycle,
  progress: r => progressPct(r),
  due: r => r.dueDate,
  status: r => statusOf(r).label,
  assigned: r => r.assigneeName,
}

function PendingActionsTable({
  rows,
  unitLabel,
  typeLabel,
}: {
  rows: PendingActionRow[]
  unitLabel: string
  typeLabel: string
}) {
  const { tablePrefix } = useEquipmentScope()
  // Before the empty-state early return: a hook cannot be called conditionally.
  const cols = useTableColumns(`${tablePrefix}-pending-actions`, COLUMNS)
  // Held after a due date is edited in place, so the row stays put rather than
  // jumping to its new date's place (see keepRowOrder). A column the user sorts
  // by still sorts; a new resource filter is a new list, remounting this table.
  const [heldOrder, setHeldOrder] = useState<string[] | null>(null)
  const held = useMemo(() => keepRowOrder(rows, heldOrder, r => r.eventId), [rows, heldOrder])
  const sorted = useSortedRows(held, cols.sort, SORT)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { selectedTenantId } = useTenant()
  const { can } = usePermission()
  const { email } = useAuth()
  const canOpen = can(P.equipmentExecute)
  const [opening, setOpening] = useState<string | null>(null)
  const [openError, setOpenError] = useState<string | null>(null)

  // A run's page by the task's kind: a room's is a Cleaning run, anything else an Equipment run.
  const runPath = (row: PendingActionRow, procedureId: string) =>
    procedureRunPath(row.purpose === 'ROOM' ? 'Cleaning' : 'Equipment', procedureId, null)

  // The Due column, edited in place (overdue tasks especially): same route and
  // permission as the Tasks list's.
  async function reschedule(row: PendingActionRow, dueDate: string) {
    setHeldOrder(order => order ?? sorted.map(r => r.eventId))
    await equipmentApi.updateEvent(selectedTenantId as string, row.eventId, {
      dueDate,
      ...(email ? { actor: email } : {}),
    })
    await Promise.all([
      qc.invalidateQueries({ queryKey: [PENDING_ACTIONS_KEY] }),
      qc.invalidateQueries({ queryKey: ['maintenance-events'] }),
    ])
  }

  async function open(row: PendingActionRow) {
    if (opening) return
    setOpenError(null)
    if (row.procedureId) {
      const path = runPath(row, row.procedureId)
      if (path) navigate(path)
      return
    }
    setOpening(row.eventId)
    try {
      const { procedureId } = await equipmentApi.openTaskProcedure(selectedTenantId as string, row.eventId)
      await qc.invalidateQueries({ queryKey: [PENDING_ACTIONS_KEY] })
      const path = runPath(row, procedureId)
      if (path) navigate(path)
    } catch (e) {
      setOpenError(requestErrorMessage(e, "Could not open this task's procedure."))
    } finally {
      setOpening(null)
    }
  }

  if (rows.length === 0) {
    return (
      <div className='rounded-xl border bg-card p-10 text-center'>
        <Hourglass className='mx-auto h-6 w-6 text-muted-foreground' />
        <p className='mt-2 text-sm font-medium'>No pending actions</p>
        <p className='mt-1 text-xs text-muted-foreground'>
          A task appears here when its {typeLabel.toLowerCase()}'s cycle carries a procedure, from the day it is due
          until it ends.
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      {openError && (
        <div className='flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          {openError}
        </div>
      )}
      <div className='rounded-xl border bg-card'>
        <Table className='min-w-[1000px]'>
          <TableHeader>
            <TableRow>
              {cols.visible.map(({ def }) => {
                const label = def.id === 'unit' ? unitLabel : def.id === 'type' ? typeLabel : def.header
                return (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={label}
                    menuLabel='Pending action columns'
                    className={def.id === 'actions' ? 'w-20' : 'whitespace-nowrap'}
                    resizable={def.id !== 'actions'}
                  >
                    {label}
                  </ResizableTableHead>
                )
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((row, i) => {
              // Without equipment.execute an unopened run cannot be created, so
              // only a run that already exists is a link.
              const clickable = canOpen || !!row.procedureId
              const status = statusOf(row)
              const pct = progressPct(row)
              return (
                <TableRow
                  key={row.eventId}
                  role={clickable ? 'link' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  aria-busy={opening === row.eventId || undefined}
                  onClick={clickable ? () => void open(row) : undefined}
                  onKeyDown={
                    clickable
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            void open(row)
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    clickable
                      ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'
                      : 'cursor-default',
                    i % 2 === 1 && 'bg-muted/10',
                  )}
                >
                  <ColumnCells
                    columns={cols}
                    cells={{
                      unit: <TableCell className='font-mono text-xs font-semibold text-gray-900'>{row.equipmentLabel}</TableCell>,
                      type: <TableCell className='text-gray-700'>{row.equipmentTypeName}</TableCell>,
                      procedure: <TableCell className='font-medium text-gray-900'>{row.procedureTemplateName}</TableCell>,
                      cycle: <TableCell className='capitalize text-gray-600'>{row.cycle ?? '—'}</TableCell>,
                      progress: (
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <div className='h-1.5 w-20 overflow-hidden rounded-full bg-slate-200'>
                              <div className='h-full rounded-full bg-emerald-500' style={{ width: `${pct}%` }} />
                            </div>
                            <span className='text-xs tabular-nums text-muted-foreground'>
                              {row.procedureId ? `${row.completedForms}/${row.totalForms}` : `not opened · ${row.totalForms} forms`}
                            </span>
                          </div>
                        </TableCell>
                      ),
                      due: (
                        <TableCell className='whitespace-nowrap tabular-nums text-gray-600'>
                          <DueDateEditor
                            dueDate={row.dueDate}
                            earlyStart={row.earlyStart}
                            subject={row.equipmentLabel}
                            onSave={canOpen ? dueDate => reschedule(row, dueDate) : undefined}
                          >
                            {formatDueDay(row.dueDate)}
                          </DueDateEditor>
                        </TableCell>
                      ),
                      status: (
                        <TableCell>
                          <span
                            className={cn(
                              'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                              status.className,
                            )}
                          >
                            {status.label}
                          </span>
                        </TableCell>
                      ),
                      assigned: (
                        <TableCell className='text-gray-600'>
                          {row.assigneeName ?? <span className='text-muted-foreground'>Unassigned</span>}
                        </TableCell>
                      ),
                      actions: (
                        <TableCell className='text-right'>
                          {opening === row.eventId ? (
                            <Loader2 className='ml-auto h-4 w-4 animate-spin text-muted-foreground' />
                          ) : (
                            <span className='text-xs text-muted-foreground'>{clickable ? 'Open' : '—'}</span>
                          )}
                        </TableCell>
                      ),
                    }}
                  />
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
